import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

interface PaymentRequestState {
    items: any[];
    current: any | null;
    total: number;
    isLoading: boolean;
    error: string | null;
}

const initialState: PaymentRequestState = {
    items: [],
    current: null,
    total: 0,
    isLoading: false,
    error: null,
};

export const fetchPaymentRequests = createAsyncThunk(
    'paymentRequest/fetchAll',
    async (filter: any = {}, { rejectWithValue }) => {
        try {
            const response = await apiService.listPaymentRequests(filter);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const fetchPaymentRequestById = createAsyncThunk(
    'paymentRequest/fetchById',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await apiService.getPaymentRequest(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const createPaymentRequest = createAsyncThunk(
    'paymentRequest/create',
    async (input: any, { rejectWithValue }) => {
        try {
            const response = await apiService.createPaymentRequest(input);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const submitPaymentRequest = createAsyncThunk(
    'paymentRequest/submit',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await apiService.submitPaymentRequest(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const apSubmitPaymentRequest = createAsyncThunk(
    'paymentRequest/apSubmit',
    async ({ id, input }: { id: number; input: any }, { rejectWithValue }) => {
        try {
            const response = await apiService.apSubmitPaymentRequest(id, input);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const approvePaymentRequest = createAsyncThunk(
    'paymentRequest/approve',
    async ({ id, comments }: { id: number; comments?: string }, { rejectWithValue }) => {
        try {
            const response = await apiService.approvePaymentRequest(id, comments);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const rejectPaymentRequest = createAsyncThunk(
    'paymentRequest/reject',
    async ({ id, comments }: { id: number; comments: string }, { rejectWithValue }) => {
        try {
            const response = await apiService.rejectPaymentRequest(id, comments);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const returnPaymentRequest = createAsyncThunk(
    'paymentRequest/return',
    async ({ id, comments }: { id: number; comments: string }, { rejectWithValue }) => {
        try {
            const response = await apiService.returnPaymentRequest(id, comments);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

const paymentRequestSlice = createSlice({
    name: 'paymentRequest',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.current = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch list
        builder.addCase(fetchPaymentRequests.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPaymentRequests.fulfilled, (state, action: any) => {
            state.isLoading = false;
            const payload = action.payload || {};
            state.items = Array.isArray(payload.data) ? payload.data : (payload.data ?? []);
            state.total = payload.meta?.total ?? state.items.length;
        });
        builder.addCase(fetchPaymentRequests.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch by id
        builder.addCase(fetchPaymentRequestById.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPaymentRequestById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(fetchPaymentRequestById.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Create
        builder.addCase(createPaymentRequest.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createPaymentRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(createPaymentRequest.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Submit
        builder.addCase(submitPaymentRequest.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(submitPaymentRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(submitPaymentRequest.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // AP Submit
        builder.addCase(apSubmitPaymentRequest.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(apSubmitPaymentRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(apSubmitPaymentRequest.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Approve
        builder.addCase(approvePaymentRequest.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(approvePaymentRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(approvePaymentRequest.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Reject
        builder.addCase(rejectPaymentRequest.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(rejectPaymentRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(rejectPaymentRequest.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Return
        builder.addCase(returnPaymentRequest.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(returnPaymentRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(returnPaymentRequest.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });
    },
});

export const { clearCurrent, clearError } = paymentRequestSlice.actions;
export default paymentRequestSlice.reducer;
